// kiosk-surface.ts
// ============================================================
// A "kiosk surface" is the shared in-store device's route space: the /limited
// kiosk view, /kiosk, /shared-login, and the /c/:companyCode company-code
// entry.
//
// Why this matters for auth: a leftover `pin_session` in localStorage may ONLY
// be honored on these routes. Off-kiosk — the full app, or a personal native
// app — a leftover PIN must NEVER shadow an email login, or it renders that
// user as whoever last PIN'd in (reported: a Store Lead getting full kiosk
// access on their phone). AuthContext has two code paths that read
// `pin_session` (cold-start initAuth + the pin_session_updated handler); both
// must apply THIS one guard so they can't drift apart (they did — the handler
// lacked the guard, which is the bug this centralizes the fix for).

export function isKioskSurfacePath(pathname: string): boolean {
  return /^\/(limited|kiosk|shared-login|c)(\/|$)/.test(pathname);
}

// Whether a leftover `pin_session` in localStorage may be honored (turned into a
// loginMethod='pin' kiosk session). Two conditions, BOTH required:
//   * we're on the shared kiosk surface (isKioskSurfacePath), AND
//   * we are NOT the native app.
// The native app is a PERSONAL phone and is email-only — it must never run as a
// PIN kiosk, or a leftover PIN session hands a personal device full operational
// access (the scheduling-only confinement only applies to email sessions). So
// on native this is always false: no PIN session is ever established there.
export function shouldHonorLeftoverPinSession(pathname: string, isNative: boolean): boolean {
  return !isNative && isKioskSurfacePath(pathname);
}

// Whether AuthContext's signOut() should run the PIN/kiosk branch (revoke the
// pin_session token, bounce to /limited) instead of the normal email branch
// (full supabase.auth.signOut() + localStorage wipe, bounce to /auth).
//
// This must key off loginMethod, NOT merely "does a pin_session key exist" --
// a leftover key can sit in localStorage on a device that is genuinely on an
// EMAIL login (e.g. one that slipped through the kiosk PIN pad before
// #1130/#1188 closed that entry point). Treating that leftover key alone as
// proof of a PIN sign-out misroutes the email user's sign-out to the kiosk
// PIN pad (/limited) instead of /auth -- so "log out, log back in" left an
// already-confused employee looking at a PIN pad, and typing their PIN there
// (a reasonable thing to try after the app just dumped you on the kiosk
// screen) re-establishes exactly the full-kiosk-access session the original
// report was about (#1129, reopened repeatedly -- the two route-guard fixes
// closed the entry point but never touched this exit path).
//
// A leftover key is only trusted when loginMethod hasn't resolved yet (the
// cold-start race this fallback was originally meant for) AND we're on the
// kiosk surface right now -- the one place a genuine PIN sign-out button
// lives -- mirroring shouldHonorLeftoverPinSession's guard.
export function isPinSignOut(
  loginMethod: 'email' | 'pin' | null | undefined,
  hasPinSessionKey: boolean,
  pathname: string,
): boolean {
  if (loginMethod === 'email') return false;
  if (loginMethod === 'pin') return true;
  return hasPinSessionKey && isKioskSurfacePath(pathname);
}

// Whether the first-visit cookie/consent banner may render on this route.
//
// It may not on a kiosk surface. The banner is `fixed inset-x-0 bottom-0`, and
// every kiosk surface puts a PIN pad plus its submit button at the bottom of a
// vertically-centered card (KioskView's "Unlock Kiosk", EmployeeLimitedView's
// staff pad). On a fresh device — no stored consent, which is exactly the state
// a store iPad is in the first time it is set up — the banner sits on top of
// the pad's bottom row (the `0` key) and the button beneath it, so a leadership
// PIN containing a zero cannot be typed and the device cannot be unlocked.
//
// The failure is invisible server-side: nothing ever reaches `kiosk-unlock`, so
// there is no failed-attempt row and no device enrollment, and anyone debugging
// from logs concludes the PIN is simply wrong.
//
// Suppressing rather than padding around it is deliberate: these are shared
// in-store operational devices, not marketing pages. There is no individual
// visitor to give notice to (the "user" is a rotating cast of employees), and
// consent left "unset" keeps AnalyticsLoader inert — the privacy-preserving
// outcome — so nothing is collected that would need a notice.
export function shouldShowConsentBanner(pathname: string): boolean {
  return !isKioskSurfacePath(pathname);
}

// ============================================================
// A kiosk device must never run the full app as an email user
// ============================================================
// `kiosk-launch` establishes this invariant: it writes the device keys and
// then signs the launcher's email session out. Nothing maintained it
// afterwards, and until PR #1429 the launch itself left a
// `sb-<ref>-auth-token` behind that could rehydrate the session on the next
// page load — so a store tablet would wake up as the admin who set it up and
// render MainLayout at /dashboard instead of the PIN pad. Staff read that as
// "the tablet keeps signing out of kiosk mode" (Bijan, Eagle Oaks).
//
// #1429 stops NEW launches from leaving that token behind. It does nothing for
// a device already in the broken state — that one needs a fresh launch by hand.
// This is the self-heal for those: if the full app is mounting on a device that
// holds a kiosk DEVICE SESSION, evict the email session and return to /limited.
//
// The signal is deliberately `kiosk_unlocked.session_token` — the 30-day device
// session minted by kiosk-launch / SharedLogin — and NOT a leftover
// `pin_session` key. That distinction is the whole lesson of #1129: a stray
// pin_session on a personal device is not evidence of a kiosk, and treating it
// as one dumps a confused employee on a PIN pad. A device session is issued to
// a DEVICE by an explicit launch, and the only way to get one is for someone
// with kiosk-launch rights to have turned this device into a kiosk.
//
// Escape hatch, unchanged: ending kiosk mode (the kiosk logout, or revoking the
// device in Settings → Kiosk Devices) clears the device keys, after which the
// app behaves normally. So "I need to use the full app on this tablet" has an
// answer that doesn't involve fighting this guard.
// Both kiosk entry points must answer this the same way. `kiosk_unlocked` is
// written by Launch Kiosk / SharedLogin; `kiosk_unlock` by KioskView's
// /kiosk/:companyCode/:locationCode unlock. They are the same fact — this device
// was deliberately turned into a kiosk — stored under two names for historical
// reasons, and reading only one is how the pre-PIN board came to work on launched
// kiosks and silently not on unlocked ones (#1432). LimitedTeamComms already
// reads both; this is the other half.
//
// Devices unlocked before #1432 have no `session_token` in `kiosk_unlock` at all,
// so they keep answering null here and nothing changes for them until they are
// unlocked again.
const KIOSK_DEVICE_SESSION_KEYS = ['kiosk_unlocked', 'kiosk_unlock'] as const;

export function readKioskDeviceSessionToken(storage: Pick<Storage, 'getItem'>): string | null {
  for (const key of KIOSK_DEVICE_SESSION_KEYS) {
    try {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { session_token?: unknown };
      if (typeof parsed.session_token === 'string' && parsed.session_token) {
        return parsed.session_token;
      }
    } catch {
      // Corrupt JSON is not evidence of a kiosk — try the other key, then
      // leave the app alone.
      continue;
    }
  }
  return null;
}

/**
 * Should the full app evict its email session because this is a kiosk device?
 *
 * @param hasEmailSession  an email (not PIN) session is currently mounted
 * @param storage          localStorage, injected so this stays pure/testable
 */
export function shouldEvictAppSessionOnKioskDevice(
  hasEmailSession: boolean,
  storage: Pick<Storage, 'getItem'>,
): boolean {
  // Only an EMAIL session is wrong here. A PIN session on a kiosk is the
  // normal, intended state — evicting it would log out the employee mid-shift.
  if (!hasEmailSession) return false;
  return readKioskDeviceSessionToken(storage) !== null;
}

// ============================================================
// An email sign-out on a kiosk device must not end kiosk mode
// ============================================================
// signOut()'s email branch does a full localStorage.clear(), which also wipes
// the keys that make this device a kiosk (the unlock record, its device
// session, the device-registry token, the remembered company code and
// location). On a store tablet that turned an ordinary "Sign Out" into
// "take this tablet out of kiosk mode": the next visit to /limited found no
// company code and asked for one, and only a manager re-launching kiosk mode
// fixed it. The device registry shows ~20 such re-enrollments across Bijan's
// two stores between 9/9 and 10/3 (#1858).
//
// Staff reach that branch without meaning to end anything: the "Sign Out"
// item on any /limited/* page with no PIN session (#1840's confirm only
// covers exactly '/limited'), an employee whose loginMethod resolved to
// 'email' from a stale launcher JWT, or one whose loginMethod is still null
// after pin_session was already removed (isPinSignOut answers false).
//
// So the rule is: on a device that holds a kiosk DEVICE SESSION (the same
// signal shouldEvictAppSessionOnKioskDevice trusts, and for the same #1129
// reason: a stray pin_session is not proof of a kiosk, an explicit launch
// is), the email sign-out still signs the user fully out but keeps the
// device's kiosk keys and returns to the PIN pad. Ending kiosk mode is a
// separate, explicit act: the confirmed "Exit kiosk mode" menu item passes
// exitKiosk, which keeps the old full wipe.
//
// Not kept: pin_session and everything user-scoped. Those describe the
// departing person, not the device.
export const KIOSK_DEVICE_STORAGE_KEYS = [
  'kiosk_unlocked',
  'kiosk_unlock',
  'kiosk_device_token',
  'kiosk_company_code',
  'kiosk_location_id',
] as const;

export interface EmailSignOutPlan {
  /** Entries to write back after localStorage.clear(). Empty = full wipe. */
  preserve: Array<[string, string]>;
  /** Where to send the browser once signed out. */
  route: string;
}

export function planEmailSignOut(
  storage: Pick<Storage, 'getItem'>,
  options: { exitKiosk?: boolean } = {},
): EmailSignOutPlan {
  if (options.exitKiosk || readKioskDeviceSessionToken(storage) === null) {
    return { preserve: [], route: '/auth' };
  }

  const preserve: Array<[string, string]> = [];
  for (const key of KIOSK_DEVICE_STORAGE_KEYS) {
    try {
      const value = storage.getItem(key);
      if (value !== null) preserve.push([key, value]);
    } catch {
      /* unreadable key: nothing to keep */
    }
  }

  // Back to the PIN pad for this tablet's store. kiosk_location_id is what
  // Launch Kiosk / SharedLogin write; the unlock record is the fallback.
  let locationId: string | null = null;
  try {
    locationId = storage.getItem('kiosk_location_id');
  } catch {
    /* fall through */
  }
  if (!locationId) {
    for (const key of ['kiosk_unlocked', 'kiosk_unlock']) {
      try {
        const raw = storage.getItem(key);
        const id = raw ? (JSON.parse(raw) as { location_id?: unknown }).location_id : null;
        if (typeof id === 'string' && id) {
          locationId = id;
          break;
        }
      } catch {
        continue;
      }
    }
  }

  const route = locationId ? `/limited?${new URLSearchParams({ location: locationId })}` : '/limited';
  return { preserve, route };
}
